
// passive events support test
let supportsPassiveEvents = false;
try {
  var opts = Object.defineProperty({}, 'passive', {
    get: function() {
      supportsPassiveEvents = true;

      return supportsPassiveEvents;
    }
  });
  window.addEventListener('test', null, opts);

} catch (e) {
  // eslint-disable-line no-empty
}

/**
 * Windowatch is a singleton class managing scroll, resize and breakpoint change
 * events globally. It uses passive resize & scroll event listeners and
 * `requestAnimationFrame` to optimize dom interactions (get, update).
 * @author ff@taikonauten.com
 */
export class Windowatch {
  /**
   * Constructor.
   */
  constructor () {
    // window size
    this._width = null;
    this._height = null;
    this._sizeDidChange = false;
    this._resizeHandler = this.windowDidResize.bind(this);

    // breakpoint
    this._breakpoint = null;
    this._breakpointSpecs = null;
    this._breakpointDidChange = false;

    // scroll
    this._scrollY = 0;
    this._scrollDidChange = false;
    this._scrollListening = false;
    this._scrollHandler = this.windowDidScroll.bind(this);

    // next frame
    this._frameHandler = this.frame.bind(this);
    this._framePending = false;

    // listeners
    this._scrollListeners = [];
    this._scrollListenerBreakpoints = [];
    this._resizeListeners = [];
    this._resizeListening = false;
    this._breakpointListeners = [];
  }

  /**
   * Returns current window width.
   * @return {int}
   */
  getWindowWidth () {
    if (!this._resizeListening) {
      this.windowDidResize();
    }

    return this._width;
  }

  /**
   * Returns current window height.
   * @return {int}
   */
  getWindowHeight () {
    if (!this._resizeListening) {
      this.windowDidResize();
    }

    return this._height;
  }

  /**
   * Returns current breakpoint name.
   * @return {string}
   */
  getBreakpoint () {
    if (!this._resizeListening) {
      this.windowDidResize();
    }

    return this._breakpoint;
  }

  /**
   * Returns current breakpoint spec.
   * @return {object}
   */
  getBreakpointSpec () {
    if (!this._resizeListening) {
      this.windowDidResize();
    }

    return this._breakpointSpecs[this._breakpoint];
  }

  /**
   * Returns current window scrollY.
   * @return {int}
   */
  getScrollY () {
    if (!this._scrollListening) {
      this.windowDidScroll();
    }

    return this._scrollY;
  }

  /**
   * Adds a scroll listener.
   * @example
   * windowatch.addScrollListener((scroll, windowatch) => {
   *   let measurement = $element.classList.contains('block--foo');
   *   return () => {
   *     $element.classList.toggle('block--bar', measurement);
   *   }
   * });
   * @param {function(scroll: int): function} callback
   * Function called when a scroll event gets fired. The function returned by
   * that callback gets called at the end of the frame. You should do your dom
   * measurements inside the callback and the dom updates inside the function
   * returned by the callback.
   * @param {?string[]} [breakpoints] Array of breakpoint names in which this
   * listener should to get scroll events.
   * @return {Windowatch} Fluent interface
   */
  addScrollListener (callback, breakpoints = null) {
    if (this._scrollListeners.indexOf(callback) === -1) {
      this._scrollListeners.push(callback);
      this._scrollListenerBreakpoints.push(breakpoints);
      this.updateWindowListeners();
    }

    return this;
  }

  /**
   * Removes scroll listener.
   * @param {function} callback
   * @return {Windowatch} Fluent interface
   */
  removeScrollListener (callback) {
    let index = this._scrollListeners.indexOf(callback);
    if (index !== -1) {
      this._scrollListeners.splice(index, 1);
      this._scrollListenerBreakpoints.splice(index, 1);
      this.updateWindowListeners();
    }

    return this;
  }

  /**
   * Adds resize listener.
   * @param {function(width: int, height: int): function} callback
   * @return {Windowatch} Fluent interface
   */
  addResizeListener (callback) {
    if (this._resizeListeners.indexOf(callback) === -1) {
      this._resizeListeners.push(callback);
      this.updateWindowListeners();
    }

    return this;
  }

  /**
   * Removes resize listener.
   * @param {function} callback
   * @return {Windowatch} Fluent interface
   */
  removeResizeListener (callback) {
    let index = this._resizeListeners.indexOf(callback);
    if (index !== -1) {
      this._resizeListeners.splice(index, 1);
      this.updateWindowListeners();
    }

    return this;
  }

  /**
   * Sets the breakpoint specs. Needs to be called before adding any breakpoint
   * listeners or getting the current breakpoint.
   * @example
   * windowatch.setBreakpointSpecs({
   *   s: { min: 0, max: 1199 },
   *   l: { min: 1200, max: null }
   * });
   * @param {object} breakpointMap Object mapping breakpoint names to the spec
   * their respective specs.
   * @return {Windowatch} Fluent interface
   */
  setBreakpointSpecs (breakpointSpecs) {
    this._breakpointSpecs = breakpointSpecs;

    return this;
  }

  /**
   * Adds breakpoint listener.
   * @param {function(breakpoint: string, spec: object): function} callback
   * @return {Windowatch} Fluent interface
   */
  addBreakpointListener (callback) {
    if (this._breakpointListeners.indexOf(callback) === -1) {
      this._breakpointListeners.push(callback);
      this.updateWindowListeners();
    }

    return this;
  }

  /**
   * Removes breakpoint listener.
   * @param {function} callback
   * @return {Windowatch} Fluent interface
   */
  removeBreakpointListener (callback) {
    let index = this._breakpointListeners.indexOf(callback);
    if (index !== -1) {
      this._breakpointListeners.splice(index, 1);
      this.updateWindowListeners();
    }

    return this;
  }

  /**
   * Registers or removes window listeners, depending on own listener.
   * @protected
   * @return {Windowatch} Fluent interface
   */
  updateWindowListeners () {
    // count total resize listeners (scroll listeners depend on resize)
    let listenerCount =
      this._scrollListeners.length +
      this._resizeListeners.length +
      this._breakpointListeners.length;

    // register resize listener if necessary
    if (!this._resizeListening && listenerCount > 0) {
      this._resizeListening = true;
      window.addEventListener('resize', this._resizeHandler,
        supportsPassiveEvents ? { passive: true } : false);
      this.windowDidResize();
    }

    // deregister resize listener if no longer needed
    if (this._resizeListening && listenerCount === 0) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeListening = false;
    }

    // count scroll listeners for current breakpoint
    let scrollListenerCount = 0;
    this._scrollListeners.forEach((callback, index) => {
      let breakpoints = this._scrollListenerBreakpoints[index];
      if (breakpoints === null ||
          breakpoints.indexOf(this._breakpoint) !== -1) {
        scrollListenerCount++;
      }
    });

    // register scroll listener if necessary
    if (!this._scrollListening && scrollListenerCount > 0) {
      this._scrollListening = true;
      window.addEventListener('scroll', this._scrollHandler,
        supportsPassiveEvents ? { passive: true } : false);
      this.windowDidScroll();
    }

    // deregister scroll listener if no longer needed
    if (this._scrollListening && scrollListenerCount === 0) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollListening = false;
    }
  }

  /**
   * Triggered at each window resize event.
   * @protected
   */
  windowDidResize () {
    // measure window width
    let width = window.innerWidth;
    let height = window.innerHeight;
    if (this._width === width && this._height === height) {
      // nothing changed, nothing to do
      return;
    }

    // measure window height, too
    this._width = width;
    this._height = height;
    this._sizeDidChange = true;
    this.requestFrame();

    // choose breakpoint
    let breakpoint = Object.keys(this._breakpointSpecs).find(name => {
      let spec = this._breakpointSpecs[name];

      return (spec.min === null || width >= spec.min) &&
             (spec.max === null || width <= spec.max);
    });

    if (breakpoint === undefined) {
      throw new Error(`No breakpoint defined for window width ${width}px`);
    }

    if (this._breakpoint === breakpoint) {
      // breakpoint did not change, stop here
      return;
    }

    this._breakpoint = breakpoint;
    this._breakpointDidChange = true;
    this.updateWindowListeners();
  }

  /**
   * Triggered at each window scroll event.
   * @protected
   */
  windowDidScroll () {
    let scrollY = window.pageYOffset;
    if (this._scrollY === scrollY) {
      // nothing changed, nothing to do
      return;
    }

    this._scrollY = scrollY;
    this._scrollDidChange = true;
    this.requestFrame();
  }

  /**
   * Requests the next frame.
   * @protected
   */
  requestFrame () {
    // check if frame has already been requested
    if (!this._framePending) {
      this._framePending = true;
      window.requestAnimationFrame(this._frameHandler);
    }

    return this;
  }

  /**
   * Triggered at each frame.
   * @protected
   */
  frame () {
    this._framePending = false;

    // size, breakpoint and scroll listeners may return update callbacks
    // which should be executed at the end of the frame
    // separating dom measuring from dom updating should optimize performance
    let updateCallbacks = [];

    // notify listeners about size change
    if (this._sizeDidChange) {
      this._resizeListeners.forEach(callback =>
        updateCallbacks.push(callback(this._width, this._height)));
      this._sizeDidChange = false;
    }

    // notify listeners about breakpoint change
    if (this._breakpointDidChange) {
      let spec = this._breakpointSpecs[this._breakpoint];
      this._breakpointListeners.forEach(callback =>
        updateCallbacks.push(callback(this._breakpoint, spec)));
      this._breakpointDidChange = false;
    }

    // notify listeners about scroll change
    if (this._scrollDidChange) {
      this._scrollListeners.forEach((callback, index) => {
        let breakpoints = this._scrollListenerBreakpoints[index];
        // only notify listeners who are interested
        // in scroll events in current breakpoint
        if (breakpoints === null ||
            breakpoints.indexOf(this._breakpoint) !== -1) {
          updateCallbacks.push(callback(this._scrollY));
        }
      });
      this._scrollDidChange = false;
    }

    // filter update callbacks and trigger them at the end of the frame
    updateCallbacks
      .filter(result => typeof result === 'function')
      .forEach(callback => callback());
  }
}

// export instance
export let windowatch = new Windowatch();
